import React, { useState, useEffect, KeyboardEvent } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useMutation, useApolloClient } from '@apollo/client';

import {
    Button,
    DotLogo,
    Input,
    IconInfoCircle,
    LoadingEllipses,
} from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import {
    getWebpackEnvVar,
    locale,
    setInLocalStorage,
    getAudioUploadsLocalStorageKey,
} from '@trqk/utils';

import { AuthLayout } from '../../templates';
import { LOCAL_STORAGE_KEYS, PATHS, URL_PARAMS } from '../../../constants';
import { elementIds } from './constants';
import { initialStateData, INITIAL_STATE } from '../../../app/initialState';

import './sign-in.css';

const { SIGN_IN_WITH_COGNITO, SIGN_OUT_WITH_COGNITO } = queryStrings;

const centerColumnWidth = 350;

interface SignInProps {
    location: RouterLocation;
}

/**
 * A form that allows a user to sign in. URL parameters are used to pre-populate the email field and
 * determine which route to redirect to upon successful sign in.
 *
 * Upon successful sign in, the user is redirected to the appropriate page:
 * - if the user came from route within the app, they will be sent there
 * - else if there is a URL param for `redirect_to`, the user is sent there
 * - else the user is sent to `/insights`
 */
export const SignIn = ({ location }: SignInProps): JSX.Element => {
    const clientId = getWebpackEnvVar('web', 'AWS_APP_CLIENT_ID');
    const userPoolId = getWebpackEnvVar('web', 'AWS_USER_POOL_ID');
    // Routing
    const history = useHistory();
    const urlQuery = new URLSearchParams(useLocation().search);
    const confirmedEmail = urlQuery.get(URL_PARAMS.CONFIRMED_EMAIL);
    const tokenMissing = urlQuery.get(URL_PARAMS.TOKEN_MISSING);
    const redirectToQueryParam = urlQuery.get(URL_PARAMS.REDIRECT_TO);
    const userAlreadyExistsEmail = urlQuery.get(URL_PARAMS.USER_ALREADY_EXISTS);

    let redirectTo = location?.state?.from ?? null;

    if (redirectToQueryParam && redirectToQueryParam !== PATHS.SIGN_IN) {
        redirectTo = { pathname: redirectToQueryParam };
    }

    if (redirectTo === null || redirectTo.pathname === PATHS.SIGN_IN) {
        redirectTo = { pathname: PATHS.INSIGHTS };
    }

    let initialEmail = confirmedEmail ?? '';

    if (initialEmail.length < 1) {
        initialEmail =
            typeof userAlreadyExistsEmail === 'string'
                ? userAlreadyExistsEmail
                : '';
    }

    // Sign in form
    const [email, setEmail] = useState(initialEmail);
    const [password, setPassword] = useState('');
    const [signInErrorMessage, setSignInErrorMessage] = useState('');

    const client = useApolloClient();

    const [signOutWithCognito] = useMutation(SIGN_OUT_WITH_COGNITO);

    useEffect((): void => {
        // Ensure that the Apollo Client cache is reset and the user is signed out if they visit
        // this page
        signOutWithCognito({
            variables: {
                clientId,
                userPoolId,
            },
            context: {
                initialStateData,
                initialStateQuery: INITIAL_STATE,
            },
        });
    }, [signOutWithCognito]);

    const [signInWithCognito, { loading }] = useMutation(SIGN_IN_WITH_COGNITO, {
        onCompleted: data => {
            const error = data?.signInWithCognito?.error ?? null;
            const userId = data?.signInWithCognito?.trqkUserId ?? null;

            if (error) {
                setSignInErrorMessage(error.message);
            } else {
                setInLocalStorage(
                    getAudioUploadsLocalStorageKey(userId),
                    () => null,
                );

                // Delete any existing script blocker flags - for details, see: packages/web/src/components/modals/CheckForScriptBlocker/CheckForScriptBlocker.tsx
                setInLocalStorage(
                    LOCAL_STORAGE_KEYS.SCRIPT_BLOCKER_FLAGS,
                    () => null,
                );

                client.cache.evict({
                    fieldName: 'audioUploads',
                });

                history.push(redirectTo);
            }
        },
    });

    useEffect(() => {
        if (userAlreadyExistsEmail) {
            setSignInErrorMessage(locale.enUS.SIGN_IN.USER_ALREADY_EXISTS);
        }
    }, [userAlreadyExistsEmail]);

    const showWelcomeBack = !confirmedEmail && !tokenMissing;
    const showConfirmedEmail = confirmedEmail && !tokenMissing;

    const handleSubmit = (): void => {
        signInWithCognito({
            variables: {
                username: email,
                password,
                clientId,
                userPoolId,
                updateUiState: true,
            },
        });
    };

    return (
        <AuthLayout>
            <div className="sign-in bg-no-repeat bg-contain bg-top flex flex-col h-full z-10">
                <div className="flex-grow flex flex-col justify-center items-center">
                    <div style={{ width: centerColumnWidth }}>
                        <div className="flex justify-center">
                            <DotLogo width={260} />
                        </div>
                        {showWelcomeBack && (
                            <div className="text-white text-center text-sm font-semibold tracking-widest">
                                {locale.enUS.SIGN_IN.WELCOME_BACK}
                            </div>
                        )}
                        {showConfirmedEmail && (
                            <div className="text-white text-center font-semibold tracking-widest">
                                {locale.enUS.SIGN_IN.CONFIRMED_EMAIL}
                            </div>
                        )}
                        <form>
                            <div>
                                <h4 className="text-white font-medium text-center my-4">
                                    {locale.enUS.SIGN_IN.CTA}
                                </h4>
                                <div className="my-4" style={{ minHeight: 30 }}>
                                    {signInErrorMessage && (
                                        <div className="text-center">
                                            <span
                                                className="flex-none"
                                                style={{ height: 20 }}
                                            >
                                                <IconInfoCircle
                                                    addClasses="inline"
                                                    fillColor="white"
                                                />
                                            </span>
                                            <span className="ml-1 text-sm text-white">
                                                {signInErrorMessage}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <Input
                                    addClasses="my-2"
                                    autoComplete="username"
                                    dataCy="sign-in-username"
                                    inputId={elementIds.email}
                                    icon="email"
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    handleChange={setEmail}
                                    padding="large"
                                />
                                <Input
                                    addClasses="my-2"
                                    autoComplete="current-password"
                                    dataCy="sign-in-password"
                                    inputId={elementIds.password}
                                    icon="password"
                                    type="password"
                                    placeholder="Password"
                                    value={password}
                                    handleChange={setPassword}
                                    padding="large"
                                    handleKeyPress={(
                                        e: KeyboardEvent<Element>,
                                    ): void => {
                                        if (e.key === 'Enter') {
                                            setSignInErrorMessage('');

                                            handleSubmit();
                                        }
                                    }}
                                />
                            </div>
                            <div className="text-center">
                                <Link to={PATHS.FORGOT_PASSWORD}>
                                    <div className="text-xs text-white font-semibold underline">
                                        {
                                            locale.enUS.SIGN_IN.BUTTONS
                                                .FORGOT_PASSWORD
                                        }
                                    </div>
                                </Link>
                            </div>
                            <div className="mt-10">
                                <Button
                                    color="primary-dark-bg"
                                    handleClick={handleSubmit}
                                    id={elementIds.submit}
                                    dataCy="sign-in-submit"
                                >
                                    <div className="text-center mx-3 tracking-wider">
                                        {!loading && (
                                            <div>
                                                {
                                                    locale.enUS.SIGN_IN.BUTTONS
                                                        .SIGN_IN
                                                }
                                            </div>
                                        )}
                                        {loading && (
                                            <LoadingEllipses width={40} />
                                        )}
                                    </div>
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
};
